import { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Button,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowSelectionModel,
} from '@mui/x-data-grid';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';

import { useUser } from '@/providers/UserContext';
import { useApi } from '@/providers/ApiContext';
import {
  getAppClients,
  createAppClient,
  getAppClientKeys,
  deleteAppClientKey,
} from '@/actions/appClient';
import LoadingMask from '@/components/shared/LoadingMask';
import { useAuth } from '@/providers/AuthContext';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import AccountKeyForm from './AccountKeyForm';
import type { AppClient, AppClientKey, AppClientKeyExport } from '@/types';

interface KeysGridToolbarProps {
  onAddClick: () => void;
  onDeleteClick: () => void;
  onEditClick: () => void;
  selectedRows: string[];
}

function KeysGridToolbar({
  onAddClick,
  onDeleteClick,
  onEditClick,
  selectedRows,
}: KeysGridToolbarProps) {
  return (
    <GridToolbarContainer
      sx={{
        width: '100%',
        justifyContent: 'space-between',
        '& .MuiButtonBase-root': {
          color: 'black',
        },
      }}
    >
      <Stack direction='row' spacing={1} alignItems='center'>
        <Button
          sx={{
            padding: '3px 9px',
            '& .MuiButtonBase-root svg': {
              color: 'primary',
            },
          }}
          variant='outlined'
          onClick={onAddClick}
          color='primary'
        >
          <Stack direction='row' spacing={1} alignItems='center' sx={{}}>
            <AddCircleOutlineOutlinedIcon color='primary' />
            <Typography
              color='primary'
              variant='button'
              display='block'
              gutterBottom
            >
              Create
            </Typography>
          </Stack>
        </Button>

        <Button
          sx={{ padding: '3px 9px' }}
          onClick={onEditClick}
          disabled={!selectedRows?.length || selectedRows?.length > 1}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <EditOutlinedIcon />
            <Typography variant='button' display='block' gutterBottom>
              Edit
            </Typography>
          </Stack>
        </Button>

        <Button
          sx={{ padding: '3px 9px' }}
          onClick={onDeleteClick}
          disabled={!selectedRows?.length}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <DeleteOutlinedIcon />
            <Typography variant='button' display='block' gutterBottom>
              Delete
            </Typography>
          </Stack>
        </Button>
      </Stack>
      <Stack direction='row' spacing={0.5} alignItems='center'>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </Stack>
    </GridToolbarContainer>
  );
}

function AccountKeys() {
  const user = useUser();
  const auth = useAuth();
  const api = useApi();
  const [appClients, setAppClients] = useState<AppClient[]>([]);
  const [appClientKeys, setAppClientKeys] = useState<AppClientKey[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [creating, setCreating] = useState<boolean>(false);
  const [deletingKey, setDeletingKey] = useState<boolean>(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<string>(),
  });
  const selected_rows: string[] =
    rowSelectionModel.type === 'include'
      ? (Array.from(rowSelectionModel.ids) as string[])
      : [];
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [keyFormOpen, setKeyFormOpen] = useState<boolean>(false);
  const [editingKey, setEditingKey] = useState<AppClientKey | null>(null);

  const fetchAppClientKeys = useCallback(
    async (clientId: string) => {
      if (!api) return;
      try {
        const keys = await getAppClientKeys(api, clientId);
        setAppClientKeys(keys);
      } catch (error) {
        console.error('Error fetching app client keys:', error);
      }
    },
    [api],
  );

  const fetchAppClients = useCallback(async () => {
    if (!api) return;
    try {
      setLoading(true);
      const clients = await getAppClients(api);
      setAppClients(clients);
      if (clients.length > 0) {
        await fetchAppClientKeys(clients[0].client_id);
      }
    } catch (error) {
      console.error('Error fetching app clients:', error);
    } finally {
      setLoading(false);
    }
  }, [api, fetchAppClientKeys]);

  const handleEnableApiAccess = async () => {
    if (!api || !user?.email) return;
    try {
      setCreating(true);

      const payload = {
        name: user.email,
        allowed_scopes: auth.roles,
      };
      const newClient = await createAppClient(api, payload);
      setAppClients([newClient]);
      await fetchAppClientKeys(newClient.client_id);
    } catch (error) {
      console.error('Error creating app client:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateKey = () => {
    setEditingKey(null);
    setKeyFormOpen(true);
  };

  const handleEditKey = (key: AppClientKey) => {
    setEditingKey(key);
    setKeyFormOpen(true);
  };

  const handleEditClick = () => {
    if (selected_rows.length === 1) {
      const keyToEdit = appClientKeys.find(
        (key) => key.key_id === selected_rows[0],
      );
      if (keyToEdit) {
        handleEditKey(keyToEdit);
      }
    }
  };

  const handleKeyCreated = async (_newKey: AppClientKeyExport) => {
    // Refresh the keys list after creation
    if (appClients.length > 0) {
      await fetchAppClientKeys(appClients[0].client_id);
    }
  };

  const handleKeyUpdated = async (_updatedKey: AppClientKey) => {
    // Refresh the keys list after update
    if (appClients.length > 0) {
      await fetchAppClientKeys(appClients[0].client_id);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (appClients.length === 0 || !api) return;

    try {
      setDeletingKey(true);
      await Promise.all(
        selected_rows.map((keyId) =>
          deleteAppClientKey(api, appClients[0].client_id, keyId),
        ),
      );
      await fetchAppClientKeys(appClients[0].client_id);
      setRowSelectionModel({ type: 'include', ids: new Set<string>() });
    } catch (error) {
      console.error('Error deleting app client keys:', error);
    } finally {
      setDeletingKey(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  useEffect(() => {
    fetchAppClients();
  }, [fetchAppClients]);

  const calculateAge = (createdAt: string | undefined): number => {
    if (!createdAt) return 0;
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const renderAge = (params: GridRenderCellParams<AppClientKey>) => {
    const age = calculateAge(params.row.created_at);
    const isOld = age > 90;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
        }}
      >
        <Typography variant='body2'>
          {age} day{age !== 1 ? 's' : ''}
        </Typography>
        {isOld && (
          <WarningIcon
            color='warning'
            fontSize='small'
            titleAccess={`Key is ${age} days old (older than 90 days)`}
          />
        )}
      </Box>
    );
  };

  const columns: GridColDef<AppClientKey>[] = [
    { field: 'key_id', headerName: 'Key ID', type: 'string', flex: 2 },
    { field: 'name', headerName: 'Name', type: 'string', flex: 2 },
    {
      field: 'created_at',
      headerName: 'Created',
      type: 'dateTime',
      flex: 2,
      valueGetter: (value: string) => value && new Date(value),
    },
    {
      field: 'age',
      headerName: 'Age',
      flex: 1.5,
      renderCell: renderAge,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  if (loading) {
    return <LoadingMask />;
  }

  if (appClients.length === 0) {
    return (
      <Box width='100%' padding='15px'>
        <Box
          padding='15px'
          sx={{
            maxWidth: 600,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
          }}
        >
          <Button
            variant='contained'
            onClick={handleEnableApiAccess}
            disabled={creating}
            size='large'
          >
            {creating ? 'Enabling...' : 'Enable API Access'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          width: '100%',
        }}
      >
        <DataGrid
          rows={appClientKeys}
          columns={columns}
          getRowId={(row) => row.key_id}
          checkboxSelection
          disableRowSelectionOnClick
          disableRowSelectionExcludeModel
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelection) => {
            setRowSelectionModel(newSelection);
          }}
          sx={(theme) => ({
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: theme.palette.grey[100],
            },
          })}
          loading={loading}
          showToolbar
          slots={{
            toolbar: () => (
              <KeysGridToolbar
                onAddClick={handleCreateKey}
                onDeleteClick={handleDeleteClick}
                onEditClick={handleEditClick}
                selectedRows={selected_rows}
              />
            ),
          }}
          initialState={{
            columns: {
              columnVisibilityModel: {},
            },
          }}
        />
      </Box>

      {/* Account Key Form Dialog */}
      <AccountKeyForm
        open={keyFormOpen}
        onClose={() => setKeyFormOpen(false)}
        clientId={appClients.length > 0 ? appClients[0].client_id : null}
        editingKey={editingKey}
        onKeyCreated={handleKeyCreated}
        onKeyUpdated={handleKeyUpdated}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'
      >
        <DialogTitle id='delete-dialog-title'>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id='delete-dialog-description'>
            Are you sure you want to delete the selected API key(s)? This action
            will permanently remove the keys and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            variant='outlined'
            color='inherit'
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant='contained'
            color='primary'
            autoFocus
            disabled={deletingKey}
          >
            {deletingKey ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AccountKeys;

